import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from 'reactstrap';

import { Area, LoadedPhoto, maxZoomFor } from '../utils/ProfilePhoto';

import './PhotoCropper.scss';

interface PhotoCropperProps {
  photo: LoadedPhoto;
  busy: boolean;
  onCancel: () => void;
  onSave: (area: Area) => void;
}

// Choosing how a new profile photo sits in its circle: drag to move it, and zoom with the
// slider, a pinch or the mouse wheel. The circle is the shape it is drawn in on the TV and
// the jukebox, so what is inside it here is what everyone sees.
const PhotoCropper = ({ photo, busy, onCancel, onSave }: PhotoCropperProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const maxZoom = maxZoomFor(photo.width, photo.height);

  const onCropComplete = useCallback((_: Area, pixels: Area) => setArea(pixels), []);

  return (
    <div className="photo-cropper">
      <div className="photo-cropper-stage">
        <Cropper
          image={photo.url}
          crop={crop}
          zoom={zoom}
          minZoom={1}
          maxZoom={maxZoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          objectFit="cover"
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>

      <label className="photo-cropper-zoom">
        <span>Zoom</span>
        <input
          type="range"
          min={1}
          max={maxZoom}
          step={0.01}
          value={zoom}
          disabled={maxZoom <= 1}
          onChange={(e) => setZoom(Number(e.target.value))}
          aria-label="Zoom"
        />
      </label>
      <p className="photo-cropper-hint">Drag the photo to move it. Pinch, scroll or use the slider to zoom.</p>

      <div className="photo-cropper-actions">
        <Button color="secondary" disabled={busy} onClick={onCancel}>
          {'Cancel'}
        </Button>
        <Button color="primary" disabled={busy || !area} onClick={() => area && onSave(area)}>
          {busy ? 'Saving…' : 'Use this photo'}
        </Button>
      </div>
    </div>
  );
};

export default PhotoCropper;
